import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Upload, FileText, Building2, Layers, MapPin, Sparkles, ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import '@fontsource/manrope/400.css';
import '@fontsource/manrope/500.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ZONES = [
  { id: 'plans', label: 'Plans de Masse & Étages', accept: '.pdf,.dwg,.png,.jpg', icon: Layers, desc: 'DWG, PDF, PNG' },
  { id: 'facades', label: 'Plans de Façades', accept: '.pdf,.png,.jpg', icon: Building2, desc: 'PDF, PNG, JPG' },
  { id: 'cctp', label: 'Carnet de Détails / CCTP', accept: '.pdf', icon: FileText, desc: 'PDF uniquement' },
];

const SmartUploadPage = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState({ plans: [], facades: [], cctp: [] });
  const [address, setAddress] = useState('');
  const [projectName, setProjectName] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeStep, setAnalyzeStep] = useState('');
  const [dragOver, setDragOver] = useState(null);

  const handleDrop = (zoneId, e) => {
    e.preventDefault();
    setDragOver(null);
    const dropped = Array.from(e.dataTransfer?.files || []);
    if (dropped.length) {
      setFiles(prev => ({ ...prev, [zoneId]: [...prev[zoneId], ...dropped] }));
    }
  };

  const handleFileSelect = (zoneId, e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length) {
      setFiles(prev => ({ ...prev, [zoneId]: [...prev[zoneId], ...selected] }));
    }
    e.target.value = '';
  };

  const totalFiles = Object.values(files).flat().length;

  const handleLaunch = async () => {
    if (totalFiles === 0) { toast.error('Uploadez au moins un fichier'); return; }
    if (!projectName.trim()) { toast.error('Nom du projet requis'); return; }
    setAnalyzing(true);

    const steps = [
      'Création du projet...',
      'Upload des documents...',
      'Analyse IA des plans en cours...',
      'Extraction de la matériauthèque...',
      'Finalisation...'
    ];

    try {
      // Create project with address
      setAnalyzeStep(steps[0]);
      const projRes = await axios.post(`${API}/projects`, {
        name: projectName.trim(),
        description: '',
        address: address || ''
      }, { withCredentials: true });
      const projectId = projRes.data.project_id;

      // Upload all files
      setAnalyzeStep(steps[1]);
      for (const [, fileList] of Object.entries(files)) {
        for (const file of fileList) {
          const fd = new FormData();
          fd.append('file', file);
          await axios.post(`${API}/projects/${projectId}/files/upload`, fd, {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
      }

      // Simulate AI analysis steps
      for (let i = 2; i < steps.length; i++) {
        setAnalyzeStep(steps[i]);
        await new Promise(r => setTimeout(r, 1200));
      }

      toast.success('Projet créé ! Redirection vers la matériauthèque...');
      navigate(`/hub/materials/${projectId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
      setAnalyzing(false);
    }
  };

  if (analyzing) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <div className="text-center max-w-md">
          <div className="relative w-20 h-20 mx-auto mb-8">
            <div className="absolute inset-0 bg-[#D9A05B]/20 rounded-full animate-ping" />
            <div className="relative w-20 h-20 bg-[#D9A05B] rounded-full flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-black animate-spin" />
            </div>
          </div>
          <h2 className="text-2xl font-semibold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Analyse IA en cours</h2>
          <p className="text-white/50 text-sm mb-2">{analyzeStep}</p>
          <div className="w-48 h-1 bg-white/10 rounded-full mx-auto overflow-hidden">
            <div className="h-full bg-[#D9A05B] rounded-full animate-pulse" style={{ width: '70%' }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-white" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="smart-upload-page">
      {/* Header */}
      <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/projects')} className="text-white/40 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Nouveau Pack Projet</h1>
            <p className="text-xs text-white/40">Smart Upload — Étape 1/3</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-white/30">
          <span className="px-2.5 py-1 rounded-full bg-[#D9A05B]/20 text-[#D9A05B] font-bold">1 Upload</span>
          <ArrowRight className="w-3 h-3" />
          <span className="px-2.5 py-1 rounded-full bg-white/5">2 Matériaux</span>
          <ArrowRight className="w-3 h-3" />
          <span className="px-2.5 py-1 rounded-full bg-white/5">3 Aperçu</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {/* Project info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-white/40 font-bold block mb-2">Nom du projet</label>
            <Input
              value={projectName} onChange={(e) => setProjectName(e.target.value)}
              placeholder="Résidence Les Oliviers"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/25 h-11"
              data-testid="upload-project-name"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-white/40 font-bold block mb-2">Adresse du projet</label>
            <div className="relative">
              <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/25" />
              <Input
                value={address} onChange={(e) => setAddress(e.target.value)}
                placeholder="123 Avenue Mohammed V, Marrakech"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/25 pl-9 h-11"
                data-testid="upload-address"
              />
            </div>
          </div>
        </div>

        {/* Drop zones */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {ZONES.map(zone => {
            const Icon = zone.icon;
            const zoneFiles = files[zone.id];
            return (
              <div
                key={zone.id}
                className={`border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center min-h-[200px] transition-all cursor-pointer group ${
                  dragOver === zone.id ? 'border-[#D9A05B] bg-[#D9A05B]/10 scale-[1.02]' :
                  zoneFiles.length > 0 ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-white/15 bg-white/[0.02] hover:border-white/30 hover:bg-white/5'
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(zone.id); }}
                onDragLeave={() => setDragOver(null)}
                onDrop={(e) => handleDrop(zone.id, e)}
                onClick={() => document.getElementById(`upload-${zone.id}`).click()}
                data-testid={`dropzone-${zone.id}`}
              >
                <input
                  id={`upload-${zone.id}`}
                  type="file"
                  accept={zone.accept}
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileSelect(zone.id, e)}
                />
                {zoneFiles.length > 0 ? (
                  <>
                    <CheckCircle2 className="w-8 h-8 text-emerald-400 mb-3" />
                    <p className="text-sm font-medium text-emerald-300">{zoneFiles.length} fichier{zoneFiles.length > 1 ? 's' : ''}</p>
                    <p className="text-[10px] text-white/30 mt-1">{zoneFiles.map(f => f.name).join(', ').slice(0, 50)}</p>
                  </>
                ) : (
                  <>
                    <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mb-3 group-hover:bg-[#D9A05B]/10 transition-colors">
                      <Icon className="w-6 h-6 text-white/30 group-hover:text-[#D9A05B] transition-colors" />
                    </div>
                    <p className="text-sm font-medium text-white/70 text-center">{zone.label}</p>
                    <p className="text-[10px] text-white/30 mt-1">{zone.desc}</p>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Launch button */}
        <div className="text-center pt-4">
          <button
            className="bg-[#D9A05B] text-black font-semibold px-10 py-4 rounded-xl hover:bg-[#C78D46] transition-all shadow-[0_0_20px_rgba(217,160,91,0.3)] hover:shadow-[0_0_30px_rgba(217,160,91,0.5)] text-base inline-flex items-center gap-3 disabled:opacity-40"
            onClick={handleLaunch}
            disabled={totalFiles === 0 || !projectName.trim()}
            data-testid="launch-ai-btn"
          >
            <Sparkles className="w-5 h-5" />
            Lancer l'Analyse IA du Projet
            <ArrowRight className="w-5 h-5" />
          </button>
          <p className="text-[10px] text-white/20 mt-3">{totalFiles} fichier{totalFiles > 1 ? 's' : ''} prêt{totalFiles > 1 ? 's' : ''}</p>
        </div>
      </div>
    </div>
  );
};

export default SmartUploadPage;
